import { OrganizationRole, Tier } from 'dragonmouse-shared/interfaces';
import { selectCreditWallet, selectIsGuest, Store } from 'redux/db/reducers';
import { setActiveSceneId, showConfirmModal } from 'redux/mode/actions';
import store from 'redux/store';
import { displayPlanPricingModal } from 'redux/mode/util';
import { organization, profile } from 'routes';
import { navigate } from 'utils';
import { VITE_FORGE_URL } from './constants';

export type CreditAction =
	| { kind: 'creator'; organizationId?: string }
	| { kind: 'chatEdit'; sceneId?: string; designSystemId?: string }
	| { kind: 'tryMoonchild' };

// Given a CreditAction, find the organization (if any) and the user's role.
function findOrganizationContext(
	db: Store,
	action: CreditAction
): { organizationId: string; organizationName: string; role: OrganizationRole } | null {
	const { organizationSceneIds, organizationDesignSystemIds, organizations } = db;

	if (action.kind === 'creator' && action.organizationId) {
		const org = organizations.find((o) => o.id === action.organizationId);
		if (org) {
			return { organizationId: org.id, organizationName: org.name, role: org.role };
		}
	}

	if (action.kind === 'chatEdit') {
		if (action.sceneId) {
			for (const org of organizations) {
				const sceneIds = organizationSceneIds[org.id] || [];
				if (sceneIds.includes(action.sceneId)) {
					return { organizationId: org.id, organizationName: org.name, role: org.role };
				}
			}
		}

		if (action.designSystemId) {
			for (const org of organizations) {
				const dsIds = organizationDesignSystemIds[org.id] || [];
				if (dsIds.includes(action.designSystemId)) {
					return { organizationId: org.id, organizationName: org.name, role: org.role };
				}
			}
		}
	}

	return null;
}

// Checks if user can proceed with a credit-consuming action.
// If not, shows the appropriate modal and returns true (blocked).
// Returns false if the action can proceed.
export function interceptIfNoCredits(action: CreditAction): boolean {
	const state = store.getState();

	// Organization credit check
	const orgContext = findOrganizationContext(state.db, action);
	if (orgContext) {
		const orgWallet = state.db.organizationCreditWallets[orgContext.organizationId];
		const totalOrgCredits = orgWallet
			? orgWallet.credits + orgWallet.promotionalCredits + orgWallet.purchaseCredits
			: 0;

		if (totalOrgCredits <= 0) {
			if (orgContext.role === 'owner') {
				store.dispatch(
					showConfirmModal({
						title: `${orgContext.organizationName} has run out of credits`,
						description:
							'Your team credits will refill at the start of the next billing period, or you can buy more now.',
						submitLabel: 'Buy More',
						altActionLabel: 'Team Settings',
						onSubmit: () => {
							window.location.href = `${VITE_FORGE_URL}/stripe/credit-purchase?organizationId=${orgContext.organizationId}`;
						},
						onAltAction: () => {
							navigate(organization(orgContext.organizationId));
						},
					})
				);
			} else {
				store.dispatch(
					showConfirmModal({
						title: `${orgContext.organizationName} has run out of credits`,
						description: 'Please contact the team owner to purchase more credits.',
						submitLabel: 'OK',
						onSubmit: () => {},
					})
				);
			}
			return true;
		}
		// Org has credits, allow submission
		return false;
	}

	// Guest: always blocked (no wallet, no subscription)
	if (selectIsGuest(state.db)) {
		displayPlanPricingModal();
		return true;
	}

	// Personal credit check
	const creditWallet = selectCreditWallet(state.db);
	if (!creditWallet) {
		store.dispatch(
			showConfirmModal({
				title: 'Something went wrong',
				description: "We couldn't check your credits. Please refresh the page and try again.",
				submitLabel: 'OK',
				onSubmit: () => {},
			})
		);
		return true;
	}

	const totalCredits =
		creditWallet.credits + creditWallet.promotionalCredits + creditWallet.purchaseCredits;
	if (totalCredits > 0) {
		return false;
	}

	const tier = state.db.subscription?.tier ?? Tier.FREE;

	// Tier 1 - no credits: refill/upgrade modal
	if (tier === Tier.TIER_1) {
		store.dispatch(
			showConfirmModal({
				title: "You've run out of credits",
				description:
					'Your credits will refill at the start of your next billing period, or you can buy more now.',
				submitLabel: 'Refill Credits',
				altActionLabel: 'Upgrade Plan',
				onSubmit: () => {
					window.location.href = `${VITE_FORGE_URL}/stripe/credit-purchase`;
				},
				onAltAction: () => {
					window.location.href = `${VITE_FORGE_URL}/stripe/manage`;
				},
			})
		);
		return true;
	}

	// Tier 2 - no credits: refill modal only
	if (tier === Tier.TIER_2) {
		store.dispatch(
			showConfirmModal({
				title: "You've run out of credits",
				description:
					'Your credits will refill at the start of your next billing period, or you can buy more now.',
				submitLabel: 'Refill Credits',
				onSubmit: () => {
					window.location.href = `${VITE_FORGE_URL}/stripe/credit-purchase`;
				},
			})
		);
		return true;
	}

	// Non-subscriber: plans/pricing for creator, subscribe modal for chatEdit
	if (action.kind === 'creator') {
		displayPlanPricingModal();
		return true;
	}

	if (action.kind === 'chatEdit') {
		store.dispatch(
			showConfirmModal({
				title: 'Subscribe to continue',
				description:
					'You need an active subscription to use credits. Choose a plan to get started.',
				submitLabel: 'Subscribe',
				onSubmit: () => {
					store.dispatch(setActiveSceneId(null));
					navigate(profile());
				},
			})
		);
		return true;
	}

	// tryMoonchild for non-subscriber: N/A (they're logged in, not guest)
	return false;
}
